import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSettings, useBulkUpdateSettings } from '../../services/httpServices/queries';
import { attachmentService } from '../../services/httpServices/attachmentService';
import {
  Settings as SettingsIcon,
  Loader2,
  AlertCircle,
  Save,
  Upload,
  Image,
  Building,
  MapPin,
  Trash2,
  Globe,
  PenLine,
  Stamp,
  FileText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { TimePicker } from '../../components/ui/DatePicker';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { TIMEZONE_OPTIONS } from '../../lib/timezones';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { DOCUMENT_SIGNATURE_TARGETS } from '../../utils/signatureSettings';

const SETTING_KEYS = {
  LOGO: 'hospital_logo',
  FAVICON: 'hospital_favicon',
  NAME_EN: 'hospital_name_en',
  NAME_BN: 'hospital_name_bn',
  SHORT_NAME: 'hospital_short_name',
  ADDRESS: 'hospital_address',
  EMAIL: 'hospital_email',
  WEBSITE: 'hospital_website',
  HOTLINE: 'hospital_hotline',
  REPORT_URL: 'hospital_report_url',
  CERTIFIED: 'hospital_certified',
  TAGLINE_EN: 'hospital_tagline_en',
  TAGLINE_BN: 'hospital_tagline_bn',
  TIMEZONE: 'hospital_timezone',
  DELIVERY_LOCATION: 'hospital_delivery_location',
  RECEIPT_LEFT_NOTE: 'receipt_left_note',
  IPD_BED_CHARGE_TIME: 'ipd_bed_charge_time',
  REFERRAL_BASE_DISCOUNT_PERCENT: 'referral_base_discount_percent',
  SIGNATURE: 'hospital_signature',
  SEAL: 'hospital_seal',
  SIGNATORY_NAME: 'hospital_signatory_name',
  DOCUMENTS_SIGNATURE_ENABLED: 'documents_signature_enabled',
  DOCUMENTS_SIGNATURE_ADMISSION: 'documents_signature_admission',
  DOCUMENTS_SIGNATURE_SALARY_SHEET: 'documents_signature_salary_sheet',
  DOCUMENTS_SIGNATURE_EXPENSE_VOUCHER: 'documents_signature_expense_voucher',
  DOCUMENTS_SIGNATURE_DOCTOR_VOUCHER: 'documents_signature_doctor_voucher',
  DOCUMENTS_SIGNATURE_PAYSLIP: 'documents_signature_payslip',
  DOCUMENTS_SIGNATURE_DIAGNOSTIC_RECEIPT: 'documents_signature_diagnostic_receipt',
} as const;

/** Maps each document target to its persisted form/setting key. */
const TARGET_SETTING_KEY: Record<(typeof DOCUMENT_SIGNATURE_TARGETS)[number]['key'], keyof FormState> = {
  admission: SETTING_KEYS.DOCUMENTS_SIGNATURE_ADMISSION,
  salary_sheet: SETTING_KEYS.DOCUMENTS_SIGNATURE_SALARY_SHEET,
  expense_voucher: SETTING_KEYS.DOCUMENTS_SIGNATURE_EXPENSE_VOUCHER,
  doctor_voucher: SETTING_KEYS.DOCUMENTS_SIGNATURE_DOCTOR_VOUCHER,
  payslip: SETTING_KEYS.DOCUMENTS_SIGNATURE_PAYSLIP,
  diagnostic_receipt: SETTING_KEYS.DOCUMENTS_SIGNATURE_DIAGNOSTIC_RECEIPT,
};

/**
 * Setting keys whose value is a persisted boolean flag (`'true'` / `'false'`),
 * stored so the choice survives reloads instead of being re-selected each time.
 */
const BOOLEAN_SETTING_KEYS: string[] = [SETTING_KEYS.DOCUMENTS_SIGNATURE_ENABLED, ...Object.values(TARGET_SETTING_KEY)];

/** Tabs on the General Settings page, each addressable via `?tab=`. */
const SETTINGS_TABS = [
  { key: 'general', label: 'General', icon: Building },
  { key: 'invoice', label: 'Invoice', icon: PenLine },
  { key: 'documents', label: 'Documents', icon: FileText },
] as const;

type SettingsTabKey = (typeof SETTINGS_TABS)[number]['key'];

const DEFAULT_LABELS: Record<string, string> = {
  [SETTING_KEYS.LOGO]: 'Hospital Logo',
  [SETTING_KEYS.FAVICON]: 'Browser Favicon',
  [SETTING_KEYS.NAME_EN]: 'Hospital Name (English)',
  [SETTING_KEYS.NAME_BN]: 'Hospital Name (Bangla)',
  [SETTING_KEYS.SHORT_NAME]: 'Short Name (for receipts)',
  [SETTING_KEYS.ADDRESS]: 'Address',
  [SETTING_KEYS.EMAIL]: 'Email Address',
  [SETTING_KEYS.WEBSITE]: 'Website',
  [SETTING_KEYS.HOTLINE]: 'Hotline Number',
  [SETTING_KEYS.REPORT_URL]: 'Online Report URL',
  [SETTING_KEYS.CERTIFIED]: 'Certification Text',
  [SETTING_KEYS.TAGLINE_EN]: 'Tagline (English)',
  [SETTING_KEYS.TAGLINE_BN]: 'Tagline (Bangla)',
  [SETTING_KEYS.TIMEZONE]: 'Timezone',
  [SETTING_KEYS.DELIVERY_LOCATION]: 'Report Delivery Location',
  [SETTING_KEYS.RECEIPT_LEFT_NOTE]: 'Receipt Left Side Note',
  [SETTING_KEYS.IPD_BED_CHARGE_TIME]: 'IPD Bed Charge Time',
  [SETTING_KEYS.REFERRAL_BASE_DISCOUNT_PERCENT]: 'Receipt Base Discount (%)',
  [SETTING_KEYS.SIGNATURE]: 'Authorized Signature',
  [SETTING_KEYS.SEAL]: 'Hospital Seal',
  [SETTING_KEYS.SIGNATORY_NAME]: 'Authorized Signatory',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_ENABLED]: 'Show signature & seal on documents',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_ADMISSION]: 'Signature on admission & discharge papers',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_SALARY_SHEET]: 'Signature on bank salary sheet',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_EXPENSE_VOUCHER]: 'Signature on expense payment voucher',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_DOCTOR_VOUCHER]: 'Signature on doctor payment voucher',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_PAYSLIP]: 'Signature on employee payslip',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_DIAGNOSTIC_RECEIPT]: 'Signature on diagnostic receipt',
};

interface FormState {
  [SETTING_KEYS.LOGO]: string;
  [SETTING_KEYS.FAVICON]: string;
  [SETTING_KEYS.NAME_EN]: string;
  [SETTING_KEYS.NAME_BN]: string;
  [SETTING_KEYS.SHORT_NAME]: string;
  [SETTING_KEYS.ADDRESS]: string;
  [SETTING_KEYS.EMAIL]: string;
  [SETTING_KEYS.WEBSITE]: string;
  [SETTING_KEYS.HOTLINE]: string;
  [SETTING_KEYS.REPORT_URL]: string;
  [SETTING_KEYS.CERTIFIED]: string;
  [SETTING_KEYS.TAGLINE_EN]: string;
  [SETTING_KEYS.TAGLINE_BN]: string;
  [SETTING_KEYS.TIMEZONE]: string;
  [SETTING_KEYS.DELIVERY_LOCATION]: string;
  [SETTING_KEYS.RECEIPT_LEFT_NOTE]: string;
  [SETTING_KEYS.IPD_BED_CHARGE_TIME]: string;
  [SETTING_KEYS.REFERRAL_BASE_DISCOUNT_PERCENT]: string;
  [SETTING_KEYS.SIGNATURE]: string;
  [SETTING_KEYS.SEAL]: string;
  [SETTING_KEYS.SIGNATORY_NAME]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_ENABLED]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_ADMISSION]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_SALARY_SHEET]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_EXPENSE_VOUCHER]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_DOCTOR_VOUCHER]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_PAYSLIP]: string;
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_DIAGNOSTIC_RECEIPT]: string;
}

const emptyForm: FormState = {
  [SETTING_KEYS.LOGO]: '',
  [SETTING_KEYS.FAVICON]: '',
  [SETTING_KEYS.NAME_EN]: '',
  [SETTING_KEYS.NAME_BN]: '',
  [SETTING_KEYS.SHORT_NAME]: '',
  [SETTING_KEYS.ADDRESS]: '',
  [SETTING_KEYS.EMAIL]: '',
  [SETTING_KEYS.WEBSITE]: '',
  [SETTING_KEYS.HOTLINE]: '',
  [SETTING_KEYS.REPORT_URL]: '',
  [SETTING_KEYS.CERTIFIED]: '',
  [SETTING_KEYS.TAGLINE_EN]: '',
  [SETTING_KEYS.TAGLINE_BN]: '',
  [SETTING_KEYS.TIMEZONE]: 'Asia/Dhaka',
  [SETTING_KEYS.DELIVERY_LOCATION]: '',
  [SETTING_KEYS.RECEIPT_LEFT_NOTE]: 'ডেলিভারির তারিখ থেকে ৩০ দিনের মধ্যে রিপোর্ট সংগ্রহ করুন।',
  [SETTING_KEYS.IPD_BED_CHARGE_TIME]: '00:00',
  [SETTING_KEYS.REFERRAL_BASE_DISCOUNT_PERCENT]: '40',
  [SETTING_KEYS.SIGNATURE]: '',
  [SETTING_KEYS.SEAL]: '',
  [SETTING_KEYS.SIGNATORY_NAME]: '',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_ENABLED]: 'false',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_ADMISSION]: 'false',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_SALARY_SHEET]: 'false',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_EXPENSE_VOUCHER]: 'false',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_DOCTOR_VOUCHER]: 'false',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_PAYSLIP]: 'false',
  [SETTING_KEYS.DOCUMENTS_SIGNATURE_DIAGNOSTIC_RECEIPT]: 'false',
};

// Timezone options are shared with the Backup schedule — see lib/timezones.ts.

export function GeneralSettings() {
  const { data: settingsData, isLoading: settingsLoading, error: settingsError } = useSettings();
  const bulkUpdate = useBulkUpdateSettings();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsTabKey = SETTINGS_TABS.some((t) => t.key === tabParam)
    ? (tabParam as SettingsTabKey)
    : 'general';
  /** Keeps the active tab in the URL so it can be bookmarked/shared. */
  const setTab = (next: SettingsTabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'general') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [sealFile, setSealFile] = useState<File | null>(null);
  const [uploadingSeal, setUploadingSeal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const logoPreviewUrl = useMemo(() => {
    return logoFile ? URL.createObjectURL(logoFile) : null;
  }, [logoFile]);

  const faviconPreviewUrl = useMemo(() => {
    return faviconFile ? URL.createObjectURL(faviconFile) : null;
  }, [faviconFile]);

  const signaturePreviewUrl = useMemo(() => {
    return signatureFile ? URL.createObjectURL(signatureFile) : null;
  }, [signatureFile]);

  const sealPreviewUrl = useMemo(() => {
    return sealFile ? URL.createObjectURL(sealFile) : null;
  }, [sealFile]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (faviconPreviewUrl) {
        URL.revokeObjectURL(faviconPreviewUrl);
      }
    };
  }, [faviconPreviewUrl]);

  useEffect(() => {
    return () => {
      if (signaturePreviewUrl) {
        URL.revokeObjectURL(signaturePreviewUrl);
      }
    };
  }, [signaturePreviewUrl]);

  useEffect(() => {
    return () => {
      if (sealPreviewUrl) {
        URL.revokeObjectURL(sealPreviewUrl);
      }
    };
  }, [sealPreviewUrl]);

  useEffect(() => {
    if (settingsData?.data) {
      const next: FormState = { ...emptyForm };
      const present = new Set<string>();
      settingsData.data.forEach((s: any) => {
        if (s.key in next) {
          next[s.key as keyof FormState] = s.value || '';
          present.add(s.key);
        }
      });
      // Per-document switches without their own saved value follow the global
      // default, so the UI reflects what documents actually get.
      const globalDefault = next[SETTING_KEYS.DOCUMENTS_SIGNATURE_ENABLED] === 'true' ? 'true' : 'false';
      for (const { key } of DOCUMENT_SIGNATURE_TARGETS) {
        const settingKey = TARGET_SETTING_KEY[key];
        if (!present.has(settingKey)) {
          next[settingKey] = globalDefault;
        }
      }
      setForm(next);
      setHasChanges(false);
    }
  }, [settingsData]);

  const handleChange = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      setHasChanges(true);
      return next;
    });
  };

  const handleLogoSelect = (file: File | null) => {
    setLogoFile(file);
    setHasChanges(true);
  };

  const handleFaviconSelect = (file: File | null) => {
    setFaviconFile(file);
    setHasChanges(true);
  };

  const handleSignatureSelect = (file: File | null) => {
    setSignatureFile(file);
    setHasChanges(true);
  };

  const handleSealSelect = (file: File | null) => {
    setSealFile(file);
    setHasChanges(true);
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile) return null;
    setUploadingLogo(true);
    try {
      const res = await attachmentService.uploadFile(logoFile, 'general', 'hospital-settings', 'Hospital logo');
      return res.data?.publicUrl || null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload logo');
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const uploadFavicon = async (): Promise<string | null> => {
    if (!faviconFile) return null;
    setUploadingFavicon(true);
    try {
      const res = await attachmentService.uploadFile(faviconFile, 'general', 'hospital-settings', 'Hospital favicon');
      return res.data?.publicUrl || null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload favicon');
      return null;
    } finally {
      setUploadingFavicon(false);
    }
  };

  const uploadSignature = async (): Promise<string | null> => {
    if (!signatureFile) return null;
    setUploadingSignature(true);
    try {
      const res = await attachmentService.uploadFile(
        signatureFile,
        'general',
        'hospital-settings',
        'Authorized signature',
      );
      return res.data?.publicUrl || null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload signature');
      return null;
    } finally {
      setUploadingSignature(false);
    }
  };

  const uploadSeal = async (): Promise<string | null> => {
    if (!sealFile) return null;
    setUploadingSeal(true);
    try {
      const res = await attachmentService.uploadFile(sealFile, 'general', 'hospital-settings', 'Hospital seal');
      return res.data?.publicUrl || null;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload seal');
      return null;
    } finally {
      setUploadingSeal(false);
    }
  };

  const handleSave = async () => {
    try {
      // Upload any newly selected images and resolve their final URLs.
      const uploadedUrls: Partial<Record<keyof FormState, string>> = {};

      if (logoFile) {
        const uploaded = await uploadLogo();
        if (uploaded) uploadedUrls[SETTING_KEYS.LOGO] = uploaded;
      }
      if (faviconFile) {
        const uploaded = await uploadFavicon();
        if (uploaded) uploadedUrls[SETTING_KEYS.FAVICON] = uploaded;
      }
      if (signatureFile) {
        const uploaded = await uploadSignature();
        if (uploaded) uploadedUrls[SETTING_KEYS.SIGNATURE] = uploaded;
      }
      if (sealFile) {
        const uploaded = await uploadSeal();
        if (uploaded) uploadedUrls[SETTING_KEYS.SEAL] = uploaded;
      }

      const IMAGE_KEYS: string[] = [SETTING_KEYS.LOGO, SETTING_KEYS.FAVICON, SETTING_KEYS.SIGNATURE, SETTING_KEYS.SEAL];

      const payload = Object.keys(SETTING_KEYS).map((k) => {
        const key = SETTING_KEYS[k as keyof typeof SETTING_KEYS];
        const uploaded = uploadedUrls[key as keyof FormState];
        return {
          key,
          value: uploaded ?? form[key],
          label: DEFAULT_LABELS[key],
          valueType: IMAGE_KEYS.includes(key) ? 'image_url' : BOOLEAN_SETTING_KEYS.includes(key) ? 'boolean' : 'text',
          group: 'general',
        };
      });

      await bulkUpdate.mutateAsync(payload);
      setLogoFile(null);
      setFaviconFile(null);
      setSignatureFile(null);
      setSealFile(null);
      setHasChanges(false);
    } catch (err: any) {
      // error toast handled by global MutationCache
    }
  };

  const handleRemoveLogo = () => {
    setForm((prev) => ({ ...prev, [SETTING_KEYS.LOGO]: '' }));
    setLogoFile(null);
    setHasChanges(true);
  };

  const handleRemoveFavicon = () => {
    setForm((prev) => ({ ...prev, [SETTING_KEYS.FAVICON]: '' }));
    setFaviconFile(null);
    setHasChanges(true);
  };

  const handleRemoveSignature = () => {
    setForm((prev) => ({ ...prev, [SETTING_KEYS.SIGNATURE]: '' }));
    setSignatureFile(null);
    setHasChanges(true);
  };

  const handleRemoveSeal = () => {
    setForm((prev) => ({ ...prev, [SETTING_KEYS.SEAL]: '' }));
    setSealFile(null);
    setHasChanges(true);
  };

  /** The "Apply to all" switch reads on only when every document is allowed. */
  const allDocumentsSignatureEnabled = DOCUMENT_SIGNATURE_TARGETS.every(
    ({ key }) => form[TARGET_SETTING_KEY[key]] === 'true',
  );

  /** Flip the global default and every per-document key at once. */
  const handleToggleAllDocumentsSignature = () => {
    const next = allDocumentsSignatureEnabled ? 'false' : 'true';
    setForm((prev) => {
      const merged = {
        ...prev,
        [SETTING_KEYS.DOCUMENTS_SIGNATURE_ENABLED]: next,
      } as FormState;
      for (const { key } of DOCUMENT_SIGNATURE_TARGETS) {
        merged[TARGET_SETTING_KEY[key]] = next;
      }
      return merged;
    });
    setHasChanges(true);
  };

  /** Flip a single document type without touching the others. */
  const handleToggleDocumentTarget = (targetKey: keyof FormState) => {
    handleChange(targetKey, form[targetKey] === 'true' ? 'false' : 'true');
  };

  if (settingsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">General Settings</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load settings</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">General Settings</h1>
        </div>
        <Button
          onClick={handleSave}
          disabled={
            bulkUpdate.isPending ||
            uploadingLogo ||
            uploadingFavicon ||
            uploadingSignature ||
            uploadingSeal ||
            !hasChanges
          }
          isLoading={bulkUpdate.isPending || uploadingLogo || uploadingFavicon || uploadingSignature || uploadingSeal}
        >
          <Save size={16} className="mr-2" />
          Save Changes
        </Button>
      </div>

      {/* Tabs (active tab kept in the URL): ?tab=invoice | ?tab=documents */}
      <div className="flex w-fit items-center gap-1 rounded-lg border border-border bg-background p-1">
        {SETTINGS_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === key ? 'bg-primary text-white' : 'text-text-muted hover:bg-surface hover:text-text'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {settingsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {activeTab === 'general' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Logo Card */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image size={18} className="text-primary" />
                    Hospital Logo
                  </CardTitle>
                  <CardDescription>Upload your hospital logo image</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {form[SETTING_KEYS.LOGO] || logoFile ? (
                      <div className="relative">
                        <div className="aspect-square rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden">
                          <img
                            src={logoPreviewUrl || form[SETTING_KEYS.LOGO]}
                            alt="Hospital Logo"
                            className="max-w-full max-h-full object-contain p-4"
                          />
                        </div>
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute top-2 right-2 p-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors"
                          title="Remove logo"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="aspect-square rounded-xl border-2 border-dashed border-border bg-background flex flex-col items-center justify-center gap-2 p-4">
                        <Image size={32} className="text-text-muted" />
                        <p className="text-sm text-text-muted text-center">No logo uploaded</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-text">Change Logo</label>
                      <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:bg-background/80 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoSelect(e.target.files?.[0] || null)}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label htmlFor="logo-upload" className="cursor-pointer flex flex-col items-center gap-2">
                          <Upload size={20} className="text-primary" />
                          <p className="text-sm text-text-muted">
                            <span className="text-primary font-medium">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-text-muted">PNG, JPG, SVG up to 5MB</p>
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Details Card */}
              <Card className="lg:col-span-2 lg:row-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building size={18} className="text-primary" />
                    Hospital Details
                  </CardTitle>
                  <CardDescription>Manage your hospital information displayed across the system</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Hospital Name (English)"
                      placeholder="e.g. First Aid Hospital"
                      value={form[SETTING_KEYS.NAME_EN]}
                      onChange={(e) => handleChange(SETTING_KEYS.NAME_EN, e.target.value)}
                    />
                    <Input
                      label="Hospital Name (Bangla)"
                      placeholder="e.g. ফার্স্ট এইড হাসপাতাল"
                      value={form[SETTING_KEYS.NAME_BN]}
                      onChange={(e) => handleChange(SETTING_KEYS.NAME_BN, e.target.value)}
                    />
                    <Input
                      label="Short Name (for receipts)"
                      placeholder="e.g. FA"
                      value={form[SETTING_KEYS.SHORT_NAME]}
                      onChange={(e) => handleChange(SETTING_KEYS.SHORT_NAME, e.target.value)}
                    />
                    <Input
                      label="Hotline Number"
                      placeholder="e.g. 09610009620"
                      value={form[SETTING_KEYS.HOTLINE]}
                      onChange={(e) => handleChange(SETTING_KEYS.HOTLINE, e.target.value)}
                    />
                    <Input
                      label="Tagline (English)"
                      placeholder="e.g. Caring for Life"
                      value={form[SETTING_KEYS.TAGLINE_EN]}
                      onChange={(e) => handleChange(SETTING_KEYS.TAGLINE_EN, e.target.value)}
                      className="md:col-span-2"
                    />
                    <Input
                      label="Tagline (Bangla)"
                      placeholder="e.g. জীবনের যত্নে"
                      value={form[SETTING_KEYS.TAGLINE_BN]}
                      onChange={(e) => handleChange(SETTING_KEYS.TAGLINE_BN, e.target.value)}
                      className="md:col-span-2"
                    />
                    <Input
                      label="Email Address"
                      placeholder="e.g. info@firstaid-hospital.com"
                      value={form[SETTING_KEYS.EMAIL]}
                      onChange={(e) => handleChange(SETTING_KEYS.EMAIL, e.target.value)}
                    />
                    <Input
                      label="Website"
                      placeholder="e.g. https://firstaid-hospital.com"
                      value={form[SETTING_KEYS.WEBSITE]}
                      onChange={(e) => handleChange(SETTING_KEYS.WEBSITE, e.target.value)}
                      className="md:col-span-2"
                    />
                    <Input
                      label="Online Report URL"
                      placeholder="e.g. https://admin.fastaid.com.bd/reports"
                      value={form[SETTING_KEYS.REPORT_URL]}
                      onChange={(e) => handleChange(SETTING_KEYS.REPORT_URL, e.target.value)}
                      className="md:col-span-2"
                    />
                    <Input
                      label="Certification Text"
                      placeholder="e.g. ISO:9001-2015 Certified"
                      value={form[SETTING_KEYS.CERTIFIED]}
                      onChange={(e) => handleChange(SETTING_KEYS.CERTIFIED, e.target.value)}
                      className="md:col-span-2"
                    />
                    <SearchableSelect
                      label="Timezone"
                      labelClassName="mb-1 block text-sm font-medium text-text"
                      placeholder="Select timezone..."
                      value={form[SETTING_KEYS.TIMEZONE]}
                      onChange={(value) => handleChange(SETTING_KEYS.TIMEZONE, value)}
                      options={TIMEZONE_OPTIONS}
                    />
                    <Input
                      label="Report Delivery Location"
                      placeholder="e.g. রিপোর্ট ডেলিভারি ২য় তলায়"
                      value={form[SETTING_KEYS.DELIVERY_LOCATION]}
                      onChange={(e) => handleChange(SETTING_KEYS.DELIVERY_LOCATION, e.target.value)}
                    />
                    <Input
                      label="Receipt Left Side Note"
                      placeholder="e.g. ডেলিভারির তারিখ থেকে ৩০ দিনের মধ্যে রিপোর্ট সংগ্রহ করুন।"
                      value={form[SETTING_KEYS.RECEIPT_LEFT_NOTE]}
                      onChange={(e) => handleChange(SETTING_KEYS.RECEIPT_LEFT_NOTE, e.target.value)}
                      className="md:col-span-2"
                    />
                    <TimePicker
                      label="IPD Bed Charge Time"
                      value={form[SETTING_KEYS.IPD_BED_CHARGE_TIME]}
                      onChange={(value) => handleChange(SETTING_KEYS.IPD_BED_CHARGE_TIME, value)}
                    />
                    <div>
                      <Input
                        label="Receipt Base Discount (%)"
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        placeholder="40"
                        value={form[SETTING_KEYS.REFERRAL_BASE_DISCOUNT_PERCENT]}
                        onChange={(e) => handleChange(SETTING_KEYS.REFERRAL_BASE_DISCOUNT_PERCENT, e.target.value)}
                      />
                      <p className="mt-1 text-xs text-text-muted">
                        Maximum discount a diagnostic receipt may carry. The patient's item discount is capped by this base and
                        the remainder is the referral commission (e.g. 40% base − 20% patient = 20% referral).
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-text">Address</label>
                      <div className="relative">
                        <MapPin size={16} className="absolute left-3 top-3 text-text-muted" />
                        <textarea
                          rows={3}
                          placeholder="e.g. Chauddagram, Cumilla, Bangladesh"
                          value={form[SETTING_KEYS.ADDRESS]}
                          onChange={(e) => handleChange(SETTING_KEYS.ADDRESS, e.target.value)}
                          className="w-full rounded-lg border border-border bg-surface pl-9 pr-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Favicon Card */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe size={18} className="text-primary" />
                    Browser Favicon
                  </CardTitle>
                  <CardDescription>Shown in the browser tab for the website and dashboard</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Tab preview reflects favicon → logo → default fallback. */}
                    <div className="rounded-xl border border-border bg-background p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface">
                          <img
                            src={
                              faviconPreviewUrl ||
                              form[SETTING_KEYS.FAVICON] ||
                              form[SETTING_KEYS.LOGO] ||
                              '/favicon.png'
                            }
                            alt="Favicon preview"
                            className="h-6 w-6 object-contain"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="h-4 w-4 rounded bg-primary/15" />
                            <span className="truncate text-sm font-medium text-text">
                              {form[SETTING_KEYS.NAME_EN] || 'FastAid OS'}
                            </span>
                          </div>
                          <p className="text-xs text-text-muted">Browser tab preview</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-text">
                        {form[SETTING_KEYS.FAVICON] || faviconFile ? 'Change Favicon' : 'Upload Favicon'}
                      </label>
                      <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:bg-background/80 transition-colors">
                        <input
                          type="file"
                          accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/jpeg,image/webp"
                          onChange={(e) => handleFaviconSelect(e.target.files?.[0] || null)}
                          className="hidden"
                          id="favicon-upload"
                        />
                        <label htmlFor="favicon-upload" className="cursor-pointer flex flex-col items-center gap-2">
                          <Upload size={20} className="text-primary" />
                          <p className="text-sm text-text-muted">
                            <span className="text-primary font-medium">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-text-muted">Square PNG, ICO or SVG, 32×32 – 512×512, up to 5MB</p>
                        </label>
                      </div>
                      {(form[SETTING_KEYS.FAVICON] || faviconFile) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveFavicon}
                          className="w-full"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Remove Favicon
                        </Button>
                      )}
                    </div>

                    <div className="rounded-lg border border-border bg-background/60 p-3 text-xs text-text-muted space-y-1">
                      <p>The favicon is used by both the public website and this dashboard.</p>
                      <p>
                        If no favicon is uploaded, the <span className="text-text">Hospital Logo</span> is used;
                        otherwise the default FastAid icon is shown.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'invoice' && (
            /* Invoice Signature & Seal Card */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenLine size={18} className="text-primary" />
                  Invoice Signature &amp; Seal
                </CardTitle>
                <CardDescription>
                  Shown on the printed invoice and the downloaded PDF. Leave blank to hide.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Authorized signature */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-text">Authorized Signature</label>
                    {form[SETTING_KEYS.SIGNATURE] || signatureFile ? (
                      <div className="relative">
                        <div className="h-28 rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden">
                          <img
                            src={signaturePreviewUrl || form[SETTING_KEYS.SIGNATURE]}
                            alt="Authorized signature"
                            className="max-w-full max-h-full object-contain p-2"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveSignature}
                          className="absolute top-2 right-2 p-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors"
                          title="Remove signature"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="h-28 rounded-xl border-2 border-dashed border-border bg-background flex flex-col items-center justify-center gap-1 p-4">
                        <PenLine size={26} className="text-text-muted" />
                        <p className="text-xs text-text-muted">No signature uploaded</p>
                      </div>
                    )}
                    <div className="border-2 border-dashed border-border rounded-lg p-3 text-center hover:bg-background/80 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleSignatureSelect(e.target.files?.[0] || null)}
                        className="hidden"
                        id="signature-upload"
                      />
                      <label htmlFor="signature-upload" className="cursor-pointer flex flex-col items-center gap-1">
                        <Upload size={18} className="text-primary" />
                        <p className="text-xs text-text-muted">
                          <span className="text-primary font-medium">Click to upload</span> a transparent PNG of the
                          signature
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Hospital seal */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-text">Hospital Seal / Stamp</label>
                    {form[SETTING_KEYS.SEAL] || sealFile ? (
                      <div className="relative">
                        <div className="h-28 rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden">
                          <img
                            src={sealPreviewUrl || form[SETTING_KEYS.SEAL]}
                            alt="Hospital seal"
                            className="max-w-full max-h-full object-contain p-2"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveSeal}
                          className="absolute top-2 right-2 p-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors"
                          title="Remove seal"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="h-28 rounded-xl border-2 border-dashed border-border bg-background flex flex-col items-center justify-center gap-1 p-4">
                        <Stamp size={26} className="text-text-muted" />
                        <p className="text-xs text-text-muted">No seal uploaded</p>
                      </div>
                    )}
                    <div className="border-2 border-dashed border-border rounded-lg p-3 text-center hover:bg-background/80 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleSealSelect(e.target.files?.[0] || null)}
                        className="hidden"
                        id="seal-upload"
                      />
                      <label htmlFor="seal-upload" className="cursor-pointer flex flex-col items-center gap-1">
                        <Upload size={18} className="text-primary" />
                        <p className="text-xs text-text-muted">
                          <span className="text-primary font-medium">Click to upload</span> a seal / stamp image
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Signatory name */}
                  <div className="md:col-span-2">
                    <Input
                      label="Authorized Signatory (name / designation)"
                      placeholder="e.g. ASHIQUR RAHMAN, Managing Director"
                      value={form[SETTING_KEYS.SIGNATORY_NAME]}
                      onChange={(e) => handleChange(SETTING_KEYS.SIGNATORY_NAME, e.target.value)}
                    />
                    <p className="mt-1 text-xs text-text-muted">
                      Printed under the signature line on invoices. Both the seal and signature are optional.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'documents' && (
            /* Documents Signature & Seal Card */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={18} className="text-primary" />
                  Document Signature &amp; Seal
                </CardTitle>
                <CardDescription>
                  Add the signature and seal to individual document printouts and PDFs (reports, statements, vouchers,
                  payslips…). They are only added when allowed below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Global default + per-document switches — all saved with the settings */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-background p-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text">Apply to all documents</p>
                      <p className="mt-1 text-xs text-text-muted">
                        Turns the signature &amp; seal on or off for every document below at once. Use the switches in
                        the list to allow documents individually.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={allDocumentsSignatureEnabled}
                      aria-label="Apply signature and seal to all documents"
                      onClick={handleToggleAllDocumentsSignature}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                        allDocumentsSignatureEnabled ? 'bg-primary' : 'bg-border'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          allDocumentsSignatureEnabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-border">
                    {DOCUMENT_SIGNATURE_TARGETS.map(({ key, label }) => {
                      const settingKey = TARGET_SETTING_KEY[key];
                      const enabled = form[settingKey] === 'true';
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between gap-4 border-b border-border bg-background px-4 py-3 last:border-b-0"
                        >
                          <p className="text-sm text-text">{label}</p>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={enabled}
                            aria-label={`Add signature and seal to ${label}`}
                            onClick={() => handleToggleDocumentTarget(settingKey)}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                              enabled ? 'bg-primary' : 'bg-border'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                enabled ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-text-muted">
                    Every document uses the same signature, seal and signatory configured on the Invoice tab — the
                    switches only control which documents include them. Changes are saved with the settings.
                  </p>
                </div>

                {/* Shared assets preview (managed on the Invoice tab) */}
                <div>
                  <p className="mb-3 text-sm font-medium text-text">Using these assets</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <p className="text-xs text-text-muted">Authorized Signature</p>
                      <div className="h-24 rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden">
                        {form[SETTING_KEYS.SIGNATURE] ? (
                          <img
                            src={form[SETTING_KEYS.SIGNATURE]}
                            alt="Authorized signature"
                            className="max-w-full max-h-full object-contain p-2"
                          />
                        ) : (
                          <span className="text-xs text-text-muted">Not uploaded</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs text-text-muted">Hospital Seal / Stamp</p>
                      <div className="h-24 rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden">
                        {form[SETTING_KEYS.SEAL] ? (
                          <img
                            src={form[SETTING_KEYS.SEAL]}
                            alt="Hospital seal"
                            className="max-w-full max-h-full object-contain p-2"
                          />
                        ) : (
                          <span className="text-xs text-text-muted">Not uploaded</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs text-text-muted">Authorized Signatory</p>
                      <div className="h-24 rounded-xl border border-border bg-background flex items-center justify-center p-3 text-center">
                        <span className="text-sm text-text">{form[SETTING_KEYS.SIGNATORY_NAME] || 'Not set'}</span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-text-muted">
                    Upload or change these on the{' '}
                    <button
                      type="button"
                      onClick={() => setTab('invoice')}
                      className="font-medium text-primary hover:underline"
                    >
                      Invoice tab
                    </button>
                    .
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
